import { DoiTag } from '@/components/identifierTag';
import { HelpLine } from '@/components/helpText';
import { HyperText } from '@/components/hyperText';
import Properties, { Term as T, Value as V } from '@/components/properties';
import { Button } from '@/components/ui/button';
import { DynamicLink } from '@/reactRouterPlugins';
import React, { useState } from 'react';
import { MdLink } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { LongDate } from '@/components/dateFormats';
import { DatasetKeyLoaderResult } from '../datasetKey';

type Props = {
  dataset: DatasetKeyLoaderResult['data']['dataset'];
};

export function Registration({ dataset }: Props) {
  const { doi, created, modified, pubDate, installation } = dataset;
  const machineTags = dataset.machineTags ?? [];
  const endpoints = dataset.endpoints ?? [];
  const identifiers = dataset.identifiers ?? [];
  const hostingOrganization = installation?.organization;
  const urlEndpoints = endpoints.filter((x) => x.url);
  const availableIdentifiers = identifiers.filter(
    (x): x is NonNullable<typeof x> =>
      !!x?.type && ['DOI', 'URL', 'LSID', 'FTP', 'UNKNOWN'].indexOf(x.type) > -1
  );

  const orphanMachineTag = machineTags.find(
    (machineTag) => machineTag.namespace === 'orphans.gbif.org' && machineTag.name === 'status'
  );
  const hostingStatus = orphanMachineTag ? orphanMachineTag.value : undefined;

  // I really dislike "show all"-buttons that only show me one more item. Just show the damn item to begin with then. It is such a disappointing experience.
  // So instead we do: if less than 10 items then show them all. If above 10, then show 5 + expand button.
  // then it feels like you are rewarded for your action
  const [threshold, setThreshold] = useState(5);
  const visibleIdentifiers =
    availableIdentifiers.length < 10
      ? availableIdentifiers
      : availableIdentifiers.slice(0, threshold);
  const hasHidden = availableIdentifiers.length > visibleIdentifiers.length;

  return (
    <div>
      <Properties className="dataProse [&_a]:g-underline" useDefaultTermWidths>
        {created && (
          <>
            <T>
              <FormattedMessage id="dataset.registry.registrationDate" />
            </T>
            <V>
              <LongDate value={created} />
            </V>
          </>
        )}

        {modified && (
          <>
            <T>
              <FormattedMessage id="dataset.registry.metdataLastModified" />
            </T>
            <V>
              <LongDate value={modified} />
            </V>
          </>
        )}

        {pubDate && (
          <>
            <T>
              <FormattedMessage id="dataset.registry.pubDate" />
            </T>
            <V>
              <LongDate value={pubDate} />
            </V>
          </>
        )}

        {hostingOrganization && (
          <>
            <T>
              <FormattedMessage id="dataset.registry.hostedBy" />
            </T>
            <V>{hostingOrganization?.title}</V>
          </>
        )}

        {installation && (
          <>
            <T>
              <FormattedMessage id="dataset.registry.installation" />
            </T>
            <V>
              <DynamicLink
                to={`/installation/${installation?.key}`}
                pageId="installationKey"
                variables={{ key: installation?.key }}
                className="g-underline"
              >
                {installation?.title}
              </DynamicLink>
            </V>
          </>
        )}

        {urlEndpoints.length > 0 && (
          <>
            <T>
              <FormattedMessage id="dataset.registry.endpoints" />
            </T>
            <V>
              <Properties breakpoint={800} horizontal={false}>
                {urlEndpoints.map((endpoint) => (
                  <React.Fragment key={endpoint.key}>
                    <T>
                      <FormattedMessage id={`enums.endpointType.${endpoint.type}`} />
                    </T>
                    <V>
                      <HyperText text={endpoint.url} />
                    </V>
                  </React.Fragment>
                ))}
              </Properties>
            </V>
          </>
        )}

        {hostingStatus && (
          <>
            <T>
              <FormattedMessage id="dataset.registry.hostingStatus" />
            </T>
            <V>
              <FormattedMessage id={`dataset.registry.${hostingStatus}`} />
              <HelpLine id="what-is-an-orphan-dataset" icon />
            </V>
          </>
        )}

        {doi && (
          <>
            <T>
              <FormattedMessage id="dataset.registry.preferredIdentifier" />
            </T>
            <V>
              <DoiTag id={doi} style={{ textDecoration: 'none' }} />
            </V>
          </>
        )}

        {visibleIdentifiers.length > 0 && (
          <>
            <T>
              <FormattedMessage id="dataset.registry.alternativeIdentifier" />
            </T>
            <V>
              <Properties breakpoint={800} horizontal={false}>
                {visibleIdentifiers.map((identifier) => (
                  <React.Fragment key={identifier.key}>
                    {/* <T>{ identifier.type }</T> */}
                    <V>
                      <HyperText text={identifier.identifier} style={{ textDecoration: 'none' }} />
                    </V>
                  </React.Fragment>
                ))}

                {hasHidden && (
                  <>
                    <br />
                    <Button onClick={() => setThreshold(500)}>
                      <FormattedMessage id="phrases.showAll" />
                    </Button>
                  </>
                )}
              </Properties>
            </V>
          </>
        )}
      </Properties>

      <div className="g-mt-6">
        <Button asChild variant="outline">
          <a
            className="g-text-inherit"
            href={`${import.meta.env.PUBLIC_REGISTRY}/dataset/${dataset.key}/ingestion-history`}
          >
            <FormattedMessage id="dataset.registry.registrationDetails" />{' '}
            <MdLink className="g-ms-2" />
          </a>
        </Button>
      </div>
    </div>
  );
}
